/**
 * The named sub-assemblies a generated rung carries, and what can be done to
 * them: the propeller, the pilot, the machine guns, the cockpit interior and the
 * rudder.
 *
 * These are the only parts of an enhanced `.glb` the app addresses *by
 * name* rather than as geometry. `tools/blender/rb_enhanced/detail.py` gives
 * each its own node so that each can be its own draw call, and each its own
 * draw call so that each can be *no* draw call — because for every one of them
 * there is a view in which the port draws its own version instead and the
 * model's would stand behind it. The three doc comments below are the three
 * arguments for that, one per part.
 *
 * Split out of `enhanced-model.ts` when the guns became the third: that file is
 * about *loading* a `.glb` and making its materials the app's own, and a
 * by-name registry of sub-assemblies is a different job that had grown to half
 * its length. Nothing here loads, paints or disposes anything — every function
 * is a `traverse` and a property write, and each returns how many nodes it
 * found so a caller can tell "hidden" apart from "this model never had one".
 */

import * as THREE from "three/webgpu";

/**
 * The node a rung's **modelled propeller** rides on
 * (`tools/blender/rb_enhanced/detail.py`'s `PROP_NODE_PREFIX`), parented to that
 * rung's own node. `PROP<rung>_<propeller>`: a Gotha and a Handley Page carry
 * two apiece and each is its own node, standing on its own measured nacelle hub.
 *
 * It is its own node so that it can be its own draw call, and its own draw call
 * so that it can be *no* draw call: the app draws a propeller of its own over
 * the nose of every aeroplane that has one (AD-012,
 * `flight/propeller-discs.ts`), and until this split the enhanced `.glb` wore
 * both at once with nothing to switch off (user report, 2026-08-24). Hiding a
 * node removes it from the render list entirely, which a shader discard on a
 * tagged vertex would not — and unlike a discard it also takes the blade out of
 * the triangles `planes/parked-aircraft.ts` projects into a ground shadow.
 *
 * It is *also* its own node because a node is the only thing carrying a
 * transform, and the four airframes AD-012's effect declines wear the modelled
 * blade as their only propeller — so it has to turn
 * (`flight/propeller-blade-spin.ts`).
 *
 * The trailing `_<propeller>` is optional in the pattern on purpose: a `.glb`
 * built between the two passes names its single propeller `PROP<rung>` and still
 * matches, and one built before either carries no such node at all, in which
 * case this matches nothing and the model draws exactly as it did.
 */
const PROPELLER_NODE_PATTERN = /^PROP\d+(?:_\d+)?$/;

/**
 * The node a rung's **pilot** rides on
 * (`tools/blender/rb_enhanced/detail.py`'s `PILOT_NODE_PREFIX`), parented to
 * that rung's own node, for the same reason the propeller is on one and a
 * different camera.
 *
 * The pilot is measured rather than invented — his head is the source model's
 * own shaded sphere — and that sphere is *also* where this port puts the
 * cockpit camera (`flight/cockpit-view.ts`'s `EYE_OFFSETS`, derived from it by
 * `packages/baker/src/cockpit-eye.ts`). So the one view that must not draw him
 * is the one 3D COCKPIT adds: with the player's own airframe rendered from the
 * seat, an un-hideable pilot is the inside of his own skull filling the frame.
 *
 * A `.glb` built before the split carries no such node and matches nothing,
 * which is why a zero return is an answer and not a fault — and so is the
 * coarsest rung of a current file, which builds no pilot at all
 * (`Detail.pilot` is false on LOD3).
 */
const PILOT_NODE_PATTERN = /^PILOT\d+$/;

/**
 * The node a rung's **machine guns** ride on
 * (`tools/blender/rb_enhanced/detail.py`'s `GUNS_NODE_PREFIX`), parented to that
 * rung's own node — the third split on the propeller's terms, from the pilot's
 * seat.
 *
 * 3D COCKPIT keeps the original's own **painted** guns. They are dashboard
 * family in `packages/baker/src/cockpit-airframe.ts` and can never be
 * classified as airframe, on any of the 22 scouts, idle or firing — the
 * invariant the user asked for after flying phase 1. But the painting is drawn
 * on a quad half a metre in front of his eye and the modelled Vickers stand on
 * the decking three metres out, so with both present the model's pair shows
 * behind the painted pair: *"suppress the gun objects in cockpit view (retain
 * them in external view) as they collide with the sprite guns"* (2026-08-26).
 *
 * A `.glb` built before the split carries no such node, and neither does an
 * airframe whose source has no gun rails to size a Spandau off (an Albatros
 * buries its guns in the decking). Both match nothing, so a zero return is an
 * answer rather than a fault — in the second case a correct one: there is no
 * modelled gun to collide with the painted one.
 */
const GUNS_NODE_PATTERN = /^GUNS\d+$/;

/**
 * Shows or hides the modelled propeller everywhere under `root` — every rung of
 * a ladder, or the single rung a fixed-size viewer kept.
 *
 * Returns how many propeller nodes it found, which is what a census or a test
 * reads to tell "hidden" apart from "this model never had one".
 */
export function setModelledPropellerVisible(root: THREE.Object3D, visible: boolean): number {
  let found = 0;
  root.traverse((child) => {
    if (!PROPELLER_NODE_PATTERN.test(child.name)) return;
    child.visible = visible;
    found++;
  });
  return found;
}

/**
 * Shows or hides the pilot everywhere under `root` — every rung of a ladder, or
 * the single rung a fixed-size viewer kept.
 *
 * Returns how many pilot nodes it found, on the same terms as
 * `setModelledPropellerVisible`: a census, so a test can tell "hidden" apart
 * from "this model was built before the split".
 */
export function setPilotVisible(root: THREE.Object3D, visible: boolean): number {
  let found = 0;
  root.traverse((child) => {
    if (!PILOT_NODE_PATTERN.test(child.name)) return;
    child.visible = visible;
    found++;
  });
  return found;
}

/**
 * Shows or hides the modelled machine guns everywhere under `root`, on the same
 * terms as the pilot and the propeller: every rung of a ladder, and a census
 * back so a test can tell "hidden" apart from "this model has no modelled gun".
 */
export function setGunsVisible(root: THREE.Object3D, visible: boolean): number {
  let found = 0;
  root.traverse((child) => {
    if (!GUNS_NODE_PATTERN.test(child.name)) return;
    child.visible = visible;
    found++;
  });
  return found;
}

/**
 * The node a rung's **cockpit interior** rides on
 * (`tools/blender/rb_enhanced/detail.py`'s `COCKPIT_NODE_PREFIX`) — floorboards,
 * seat, control column and rudder bar.
 *
 * The fourth split, and the first with no visibility rule attached: since phase
 * 3 the decking over the pilot is genuinely cut open, so the interior is what is
 * behind the hole from *every* camera and there is no view that wants it gone.
 * It is a node for the ladder's sake — `Detail.cockpit` builds none on the two
 * coarse rungs, so the whole assembly switches off at the range its opening
 * stops being resolvable — and so that a census can count it.
 */
const COCKPIT_NODE_PATTERN = /^CKPT\d+$/;

/**
 * How many cockpit-interior nodes a loaded model carries.
 *
 * A count rather than a setter, because nothing hides this one. It exists so the
 * fleet test can assert "every engaged airframe has an interior" against the
 * real `.glb`s instead of against the generator's intent — and so the one
 * airframe that legitimately has none reads as a measured exception rather than
 * as a silent zero (`rb_enhanced/cockpit.py`: the D.H.2's `fuselage` role stops
 * 0.4 m short of its own pilot).
 */
export function cockpitInteriorNodes(root: THREE.Object3D): number {
  let found = 0;
  root.traverse((child) => {
    if (COCKPIT_NODE_PATTERN.test(child.name)) found++;
  });
  return found;
}

/**
 * The three candidate spin axes, in the loaded model's own frame. Module-level
 * and shared: `Object3D.quaternion.setFromAxisAngle` only reads the vector.
 */
const SPIN_AXES: readonly THREE.Vector3[] = [
  new THREE.Vector3(1, 0, 0),
  new THREE.Vector3(0, 1, 0),
  new THREE.Vector3(0, 0, 1),
];

/**
 * Which axis a propeller node turns about, **measured off its own geometry**
 * rather than assumed from the aeroplane's forward direction.
 *
 * A propeller is a disc, and a disc is the one shape whose own bounding box
 * names its axis: the box is wide across the blades, a blade-chord deep, and
 * only a blade's *thickness* thick along the shaft. So the axis is the box's
 * smallest extent, and nothing here has to know which way the engine faces.
 *
 * That matters because four of these airframes are pushers or twins. Deriving
 * the axis from the body's nose direction would be a tractor's answer, and on
 * an F.E.2b or a Gotha nacelle the *sign* of the thrust is reversed — but a
 * sign is not an axis, and the shaft line is the same either way. Measured on
 * the real bake, every `PROP` node on every rung of all 76 airframes comes out
 * on Z, which is the model's own nose axis after the exporter's Y-up conversion
 * ((x, y, z) becomes (x, z, -y)); the numbers are in
 * `docs/engineering/enhanced-models.md` §12 and the test recomputes them.
 *
 * `geometry.boundingBox` is three.js's own cache and clones share the geometry,
 * so this is three subtractions per node per frame after the first sight of a
 * model type.
 */
function propellerSpinAxis(node: THREE.Object3D): THREE.Vector3 | undefined {
  const geometry = (node as Partial<THREE.Mesh>).geometry;
  if (!geometry) return undefined;
  if (!geometry.boundingBox) geometry.computeBoundingBox();
  const box = geometry.boundingBox;
  if (!box) return undefined;
  const extents = [box.max.x - box.min.x, box.max.y - box.min.y, box.max.z - box.min.z];
  let thinnest = 0;
  for (let axis = 1; axis < 3; axis++) {
    if ((extents[axis] as number) < (extents[thinnest] as number)) thinnest = axis;
  }
  return SPIN_AXES[thinnest];
}

/**
 * Turns every modelled propeller under `root` to `phaseRad` about its own hub.
 *
 * Absolute rather than incremental: the caller owns one phase per aeroplane
 * (`flight/propeller-blade-spin.ts`) and every rung of the ladder is set to it,
 * so a distance switch never pops the blade to a different angle and a
 * quaternion cannot drift out of unit length over a long sortie.
 *
 * The node's own translation is its measured hub, which is why this rotates the
 * node instead of the rung: a Gotha's two propellers sit 6.08 units apart and
 * each turns about its own centre.
 *
 * Returns how many it turned — the same census `setModelledPropellerVisible`
 * returns, and what a test reads to tell "still" apart from "not there".
 */
export function turnModelledPropellers(root: THREE.Object3D, phaseRad: number): number {
  let found = 0;
  root.traverse((child) => {
    if (!PROPELLER_NODE_PATTERN.test(child.name)) return;
    const axis = propellerSpinAxis(child);
    if (!axis) return;
    child.quaternion.setFromAxisAngle(axis, phaseRad);
    found++;
  });
  return found;
}

/**
 * The node a rung's **rudder** rides on
 * (`tools/blender/rb_enhanced/detail.py`'s `RUDDER_NODE_PREFIX`), parented to
 * that rung's own node and standing on its own hinge.
 *
 * The fifth named sub-assembly and the second that exists in order to *move*.
 * Fourteen of the thirty-three airframes have one — the Allied types, whose
 * source draws the rudder as its own polygons aft of the fin lamina; the other
 * nineteen draw it as part of the fin panel, so this matches nothing on them and
 * their tail is exactly the tail that shipped. `tools/blender/rb_enhanced/rudder.py`
 * carries the measurement and the reason it is not invented for the rest.
 */
const RUDDER_NODE_PATTERN = /^RDDR\d+$/;

/**
 * The rudder's hinge, **stated rather than measured**.
 *
 * `propellerSpinAxis` reads a disc's axis off its own bounding box, and applying
 * that rule here would be exactly wrong: a hinged plate's *smallest* extent is
 * its thickness, which is perpendicular to the hinge. The box would name the one
 * axis the rudder certainly does not turn about — the fault
 * `docs/engineering/cockpit-3d-design.md` §16.2 wrote down in advance for
 * whoever shipped this.
 *
 * A rudder turns about the aeroplane's vertical, which is model +Z and therefore
 * glTF (and three.js) **+Y** after `export.write_glb`'s `export_yup` conversion
 * of `(x, y, z)` to `(x, z, -y)`. That is a property of what a rudder is, not of
 * any one airframe's numbers, so there is nothing here to get wrong per model.
 */
const RUDDER_HINGE_AXIS = new THREE.Vector3(0, 1, 0);

/**
 * Deflects every modelled rudder under `root` to `angleRad` about its hinge.
 *
 * Absolute, for `turnModelledPropellers`' reason: every rung of the ladder is
 * set to the same angle, so a distance switch cannot pop the surface, and
 * nothing accumulates across a sortie.
 *
 * Returns how many it found, which is what tells a caller — and a test — "this
 * aeroplane's rudder is centred" apart from "this aeroplane has no modelled
 * rudder".
 */
export function turnModelledRudders(root: THREE.Object3D, angleRad: number): number {
  let found = 0;
  root.traverse((child) => {
    if (!RUDDER_NODE_PATTERN.test(child.name)) return;
    child.quaternion.setFromAxisAngle(RUDDER_HINGE_AXIS, angleRad);
    found++;
  });
  return found;
}

